import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Phone } from 'lucide-react';
import { Icon, Toggle } from '../../../../ds/components';
import { clsx } from '../../../../ds/clsx';
import { errorMessage } from '../../../../lib/api';
import { useSessionQuery } from '../../../../lib/session';
import { WizardButton, WizardFooter, WizardIntro } from '../../ui';
import { useCompany } from '../api';
import { useWizardNav } from '../wizardContext';
import { stepEyebrow } from '../wizardSteps';
import styles from './steps.module.css';

/**
 * Onboarding Admin 09 · Telefonie: „Jetzt starten" (the journey of Einstellungen 25 — only with a full session;
 * during the setup session the step stays open and the journey follows from Admin 11 after the 2FA) or
 * „Später erledigen" with the reminder switch.
 */
export function TelephonyStep() {
  const nav = useWizardNav();
  const navigate = useNavigate();
  const session = useSessionQuery();
  const company = useCompany();
  const [remind, setRemind] = useState(true);
  const [pending, setPending] = useState<'journey' | 'skip' | 'next' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const setupSession = session.data?.setup !== null && session.data?.setup !== undefined;
  const companyName = company.data?.companyName ?? nav.wizard.organizationName;

  async function leave(kind: 'journey' | 'skip' | 'next') {
    setPending(kind);
    setError(null);
    try {
      if (kind === 'skip') {
        await nav.complete('telephony', remind ? 'skip' : 'remind_off', 'team');
      } else if (kind === 'journey' && !setupSession) {
        await nav.complete('telephony', 'continue', 'team');
        await navigate('/settings/organization/telephony');
      } else {
        await nav.complete('telephony', 'continue', 'team');
      }
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(null);
    }
  }

  return (
    <>
      <WizardIntro
        eyebrow={stepEyebrow('telephony')}
        title="Telefonie und Nachrichtenversand"
        textWidth={640}
        text="Für Anrufe und Vorlagen-Versand aus DAMOKLES brauchen Sie ein eigenes Twilio-Konto. Die Einrichtung dauert etwa 30 Minuten plus 1 bis 3 Werktage Prüfzeit bei Twilio — deshalb liegt sie in den Einstellungen und nicht hier im Wizard."
      />
      <div className={styles.choiceGrid}>
        <section className={clsx(styles.choice, styles.choiceLead)} aria-labelledby="telephony-now">
          <span className={styles.choiceIcon} aria-hidden="true">
            <Icon icon={Phone} size={16} strokeWidth={1.75} />
          </span>
          <h2 id="telephony-now" className={styles.cardTitle}>
            Jetzt starten
          </h2>
          <p className={styles.choiceText}>
            Wir öffnen die Twilio-Journey in den Einstellungen. Sie können den Wizard danach fortsetzen, der
            Fortschritt bleibt erhalten.
          </p>
          <div className={styles.choiceBox}>
            <span className={styles.choiceBoxLabel}>Was dort auf Sie wartet</span>
            <span className={styles.choiceText}>
              Konto bei{' '}
              <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noreferrer">
                twilio.com
              </a>{' '}
              anlegen · Zugangsdaten hinterlegen · Geschäftsprofil einreichen (
              <a href="https://help.twilio.com/articles/360017903653" target="_blank" rel="noreferrer">
                Regulatory Bundle
              </a>
              ) · Rufnummer kaufen · Webhooks setzt DAMOKLES · Testanruf
            </span>
          </div>
          <WizardButton
            variant="primary"
            className={styles.choiceButton}
            loading={pending === 'journey'}
            onClick={() => void leave('journey')}
          >
            Journey in den Einstellungen öffnen
          </WizardButton>
          {setupSession ? (
            <span className={styles.noteLine}>
              Die Journey öffnen Sie nach dem Einrichten des zweiten Faktors über die Checkliste.
            </span>
          ) : null}
        </section>
        <section className={styles.choice} aria-labelledby="telephony-later">
          <span className={styles.choiceIcon} aria-hidden="true">
            ↷
          </span>
          <h2 id="telephony-later" className={styles.cardTitle}>
            Später erledigen
          </h2>
          <p className={styles.choiceText}>
            Ohne Twilio funktioniert DAMOKLES vollständig — nur Anrufe und Vorlagen-Versand über die
            Firmennummer fehlen. WhatsApp läuft ohnehin über die DAMOKLES-App Ihrer Nutzer.
          </p>
          <div className={styles.choiceBox}>
            <span className={styles.choiceBoxLabel}>Erinnerung</span>
            <Toggle
              size="large"
              checked={remind}
              onChange={setRemind}
              label="Kachel in der Admin-Checkliste behalten"
              className={styles.toggleText}
            />
          </div>
          <WizardButton
            variant="secondary"
            className={styles.choiceButton}
            loading={pending === 'skip'}
            onClick={() => void leave('skip')}
          >
            Schritt überspringen
          </WizardButton>
        </section>
      </div>
      <div className={styles.infoBox}>
        Die Twilio-Kosten rechnet Twilio direkt mit Ihnen ab, nicht DAMOKLES. Für die Prüfung brauchen Sie
        einen Handelsregisterauszug, der exakt auf „{companyName}" lautet.
      </div>
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
      <WizardFooter
        back={{ label: 'Zurück', onClick: () => nav.go('domain') }}
        primary={{ label: 'Weiter zum Team', loading: pending === 'next', onClick: () => void leave('next') }}
      />
    </>
  );
}
